import React, { useState } from 'react'
import { Box, Typography, useTheme, CircularProgress, IconButton, Button } from "@mui/material";
import { DataGridPro, useGridApiRef, GridSelectionModel } from "@mui/x-data-grid-pro"
import { svSE, enUS } from '@mui/x-data-grid/locales';
import { tokens } from "../../../assets/theme";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchMachineStops } from "../../../fetchers/machine"
import MessageIcon from '@mui/icons-material/Message';
import MachineStopEditDialog from "./machineStopEditDialog";
import Popup from "../../../components/Popup";
import AxionControls from '../../../components/controls/AxionControls';
import { getStopReasonCodes } from "../../stopReasonCodes/hooks/fetchers";
import { useMsal, useAccount } from "@azure/msal-react";
import { patchMachineStops } from "../../../fetchers/machine";
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from "./messages";
import { pushErrorMessage, pushNotification } from '../../../utils/notifications';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import useFeatureFlags, { isFeatureEnabled } from "../../../hooks/useFeatureFlags";
import dayjs from "dayjs";
import { formatMinutesAsDuration } from "../../../utils/durationUtils";
import { fetchCompany } from '../../../fetchers/company';
import { isoFormat, formatDateFullTime, formatDateTime, formatDate, formatTime, formatDateTimeISO } from "../../../utils/dateTimeUtils";      

const MachineStopGrid = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { accounts } = useMsal();
    const navigate = useNavigate();
    const account = useAccount(accounts[0]);
    const language = localStorage.getItem('language') || 'en';
    const translations = language === "sv" ? svSE : enUS;
    const queryClient = useQueryClient();

    const [openPopup, setOpenPopup] = useState(false);
    const [machineStop, setMachineStop] = useState({});
    const [selectionModel, setSelectionModel] = React.useState([]);
    const [stopReasonMultiUpdate, setStopReasonMultiUpdate] = useState({
        ids: [],
        stopReasonCodeId: '',
        comment: null,
        updatedBy: account.username,
        commentedBy: null
    });
    const [showBackdrop, setShowBackdrop] = useState(false);
    const { formatMessage } = useIntl();
    const columnVisibilityModel = props.fields ?? undefined;

    //MUI Grid API
    const gridApi = useGridApiRef();
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);


    const stopReasonCodesQuery = useQuery(
        ["stopReasonCodes", companyKey],
        () => getStopReasonCodes(companyKey),
        {
            staleTime: 60000,
            refetchInterval: 60000,
            keepPreviousData: true
        }
    );

    const companyQuery = useQuery({
        queryKey:  ["company", companyKey],
        queryFn: () => fetchCompany(companyKey),
        staleTime: 60000,
        refetchInterval: 60000,
        keepPreviousData: true
    });
    
    const featuresQuery = useFeatureFlags();

    if (featuresQuery.isLoading) {
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="73vh">
                <CircularProgress color="secondary" />
            </Box>
        );
    }

    const handleEditStopClick = (id) => {
        const machineStopItem = props.machinestops.data.find((rowData) => rowData.id === id);
        setMachineStop(machineStopItem);
        setOpenPopup(true);
    }

    const handleSplit = (id) => {
        navigate(`/machinestops/${id}/split`);
    }

    const handleSave = () => {
        setShowBackdrop(true);
        const machineStopPartToSave = stopReasonMultiUpdate;
        if (machineStopPartToSave.comment && machineStopPartToSave.comment.length > 0) {
            machineStopPartToSave.commentedBy = account.username;
        } else {
            machineStopPartToSave.commentedBy = null;
        }
        setStopReasonMultiUpdate(machineStopPartToSave);
        console.log(machineStopPartToSave)
        patchMachineStops(machineStopPartToSave).then((response) => {
            queryClient.invalidateQueries(["uncodedStops"], { refetchType: "all" });
            props.handleRefresh();
            setSelectionModel([]);
            setStopReasonMultiUpdate({
                ids: [],
                stopReasonCodeId: '',
                comment: null,
                updatedBy: account.username,
                commentedBy: null
            });
            setShowBackdrop(false);
            if (response && response.activityTriggered) {
                pushNotification(<FormattedMessage {...messages.activityTriggered} />);
                props.refetchActivities();
            }
        })
            .catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Could not update the stop.`);
            });
    }

    const handleSelection = (newSelectionModel) => {
        setSelectionModel(newSelectionModel);
        setStopReasonMultiUpdate({
            ...stopReasonMultiUpdate,
            ids: newSelectionModel
        });
    }

    const handleStopReasonChange = (e) => {
        setStopReasonMultiUpdate({
            ...stopReasonMultiUpdate,
            stopReasonCodeId: e.target.value
        });
    }

    const handleCommentChange = (e) => {
        setStopReasonMultiUpdate({
            ...stopReasonMultiUpdate,
            comment: e.target.value
        });
    }

    function isLight(color) {
        if (color.length === 7) {
            const rgb = [
                parseInt(color.substring(1, 3), 16),
                parseInt(color.substring(3, 5), 16),
                parseInt(color.substring(5), 16),
            ];
            const luminance =
                (0.2126 * rgb[0]) / 255 +
                (0.7152 * rgb[1]) / 255 +
                (0.0722 * rgb[2]) / 255;
            return luminance > 0.5;
        }
        return false
    }


    function formatFullDateTime(date) {
        return date ? formatDateFullTime(language, date) : "";
    }


    let filteredStopReasonCodes = [];
    if(props.machine === undefined && stopReasonCodesQuery.isFetched) {
        filteredStopReasonCodes = stopReasonCodesQuery.data;
    }
    else if (props.machine !== undefined && stopReasonCodesQuery.isFetched) {
        filteredStopReasonCodes = stopReasonCodesQuery.data.filter(x => props.machine.machineStopReasonCodes.some(y => x.id === y.stopReasonCodeId));    
    }

    const columns = [
        {
            field: "machineName",
            headerName: formatMessage(messages.machineName),
            width: 180,
            cellClassName: "name-column--cell"
        },
        {
            field: "fromDateTimeZone",
            headerName: formatMessage(messages.fromDate),
            headerAlign: "left",
            width: 180,
            valueGetter: ({ value, row }) =>
                (companyQuery.data?.timezoneWindows !== companyQuery.data?.machineBaseTimezoneWindows) ? formatFullDateTime(row?.fromDateTimeZone) : formatFullDateTime(row?.fromDate)
          
        },
        {
            field: "toDateTimeZone",
            headerName: formatMessage(messages.toDate),
            //type: "dateTime",
            headerAlign: "left",
            width: 180,
            valueGetter: ({ value, row }) =>
                (companyQuery.data?.timezoneWindows !== companyQuery.data?.machineBaseTimezoneWindows) ? formatFullDateTime(row?.toDateTimeZone) : formatFullDateTime(row?.toDate)
            
        },
        {
            field: "minutesDown",
            headerName: formatMessage(messages.duration),
            type: 'number',
            valueFormatter: ({ value }) => formatMinutesAsDuration(value),
            headerAlign: "left",
            align: "left",
            width: 120,
        },
        {
            field: "stopReasonCodeName",
            headerName: formatMessage(messages.stopReasonCode),
            width: 200,
            renderCell: (params) => {
                return (
                    <Box
                        sx={{ cursor: "pointer" }}
                        width="100%"
                        m="0 auto"
                        p="5px"
                        display="flex"
                        borderRadius="2px"
                        justifyContent="center"
                        backgroundColor={params.row.stopReasonCodeColor}
                        onClick={() => {
                            handleEditStopClick(params.row.id)
                        }}
                    >
                        <Typography
                            color={isLight(params.row.stopReasonCodeColor) ? colors.gray[900] : colors.gray[100]}
                            sx={{ ml: "5px" }}>
                            {params.row.stopReasonCodeName}
                        </Typography>
                    </Box>
                )
            }
        },
        {
            field: "itemId",
            headerName: formatMessage(messages.item),
            width: 125
        },
        {
            field: "productionOrderId",
            headerName: formatMessage(messages.productionOrder),
            width: 200
        },
        {
            field: "comment",
            headerName: "",
            align: "center",
            renderCell: (params) => {
                return (
                    <>
                        {params.row.comment &&
                            <IconButton
                                aria-label="Edit stop reason"
                                size="small"
                                sx={{
                                    "&:hover": {
                                        backgroundColor: colors.yellow[600],
                                    },
                                    backgroundColor: colors.yellow[500],
                                }}
                                onClick={() => {
                                    handleEditStopClick(params.row.id)
                                }}
                            >
                                <MessageIcon fontSize="inherit" />
                            </IconButton>
                        }
                    </>
                )
            },
            width: 50,
        },
        {
            field: "split",
            headerName: "",
            align: "center",
            renderCell: (params) => {
                return (
                    <>
                        {params.row.splitted === 1 &&
                            <IconButton
                                aria-label="Stop has been splitted"
                                size="small"
                                sx={{
                                    backgroundColor: colors.primary[500],
                                    pointerEvents: "none"
                                }}
                            >
                                <ContentCutOutlinedIcon fontSize="inherit" />
                            </IconButton>
                        }
                    </>
                )
            },
            width: 50,
        }
    ];

    if (stopReasonCodesQuery.isLoading || companyQuery.isLoading) {
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="73vh">
                <CircularProgress color="secondary" />
            </Box>
        )
    }

    if (stopReasonCodesQuery.isError) {
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="73vh">
                <Typography variant="h5" color="error">Error: {stopReasonCodesQuery.error.message}</Typography>
            </Box>
        )
    }

    if (props.machinestops.isLoading) {
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="73vh">
                <CircularProgress color="secondary" />
            </Box>
        )
    }

    if (props.machinestops.isError) {
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="73vh">
                <Typography variant="h5" color="error">Error: {props.machinestops.error.message}</Typography>
            </Box>
        )
    }

    if (props.machinestops.isSuccess) {
        return (
            <>
                {selectionModel.length > 0 && <>
                    <Box sx={{ paddingLeft: 2, display: 'grid', gridAutoColumns: '1fr', gap: 1, backgroundColor: colors.primary[400] }}>
                        <Box sx={{ gridRow: '1', gridColumn: 'span 3', paddingTop: '11px', paddingBottom: '11px' }}>
                            <AxionControls.AxionSelect
                                options={filteredStopReasonCodes.map((item) => {
                                    return {
                                        id: item.id,
                                        title: item.name,
                                        color: item.colorCode,
                                        order: item.sortOrder,
                                        group: item.stopReasonCodeGroup ? item.stopReasonCodeGroup.name : null,
                                        groupOrder: item.stopReasonCodeGroup ? item.stopReasonCodeGroup.sortOrder : null
                                    }
                                })}
                                label={<FormattedMessage {...messages.stopReasonCode} />}
                                name="stopReason"
                                value={stopReasonMultiUpdate.stopReasonCodeId}
                                othersText={<FormattedMessage {...messages.otherStopReasonCodes} />}
                                onChange={handleStopReasonChange}
                                showNoneOption={false}
                                searchField={true}
                                searchFieldPlaceholder={formatMessage({ ...messages.searchStopReasonCodes })}
                            />
                        </Box>
                        <Box sx={{ gridRow: '1', gridColumn: 'span 3' }}>
                            <AxionControls.AxionTextBox
                                label={<FormattedMessage {...messages.comment} />}
                                name="comment"
                                value={stopReasonMultiUpdate.comment}
                                onChange={handleCommentChange}
                            />
                        </Box>
                        <Box sx={{ gridRow: '1', gridColumn: '7' }}>
                            <Button
                                variant="contained"
                                sx={{
                                    marginTop: 2,
                                }}
                                onClick={handleSave}>
                                <FormattedMessage {...messages.save} />
                            </Button>
                        </Box>
                    </Box>
                </>
                }
                <Box
                    m="0px 0 0 0"
                    height={ props.height ? props.height : props.fullPage ? "85vh" : props.tabExpanded ? "65vh" : "50vh"}
                    sx={{
                        "& .MuiDataGrid-root": {
                            backgroundColor: colors.primary[400],
                            borderColor: `${colors.primary[700]} !important`,
                            borderRadius: "0px",
                            fontSize: "1.05rem",
                            border: "0 !important",
                        },
                        "& .MuiDataGrid-main": {
                            borderRadius: "0px",
                            border: "0 !important",
                            borderColor: `${colors.primary[700]} !important`,
                        },
                        "& .MuiDataGrid-cell": {
                            borderColor: `${colors.primary[700]} !important`,
                            borderWidth: "2px !important",
                        },
                        "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
                            display: "none"
                        },
                        "& .Mui-disabled": {
                            display: "none"
                        },
                        "& .name.column--cell": {
                            color: colors.greenAccent[300],
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: colors.primary[400],
                            fontSize: '1.05rem',
                            borderColor: `${colors.primary[700]} !important`,
                            borderWidth: "2px"
                        },
                        "& .MuiDataGrid-virtualScroller": {
                            backgroundColor: colors.primary[400],

                        },
                        "& .MuiDataGrid-footerContainer": {
                            borderTop: "none",
                            backgroundColor: colors.primary[600]

                        },
                        "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                            color: `${colors.gray[100]} !important`,

                        },
                        "& .super-app-theme--11": {
                            bgcolor: `${colors.yellow[600]} !important`,

                        },
                        "& .super-app-theme--undefined-stop": {
                            bgcolor: `${colors.yellow[600]} !important`,

                        },
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: `14px !important`,
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: `14px !important`,
                        },
                        "& .Mui-checked": {
                            color: "#4cceac !important",
                        },
                        "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIconButton": {
                            opacity: 0,
                            transition: "opacity 0.2s"
                          },
                          
                        "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIconButton": {
                            opacity: 1
                        },
                    }}
                >

                    <DataGridPro
                        checkboxSelection
                        apiRef={gridApi}
                        experimentalFeatures={{ newEditingApi: true }}
                        width={50}
                        rows={props.machinestops.data}
                        loading={props.machinestops.isLoading}
                        columns={columns}
                        onRowSelectionModelChange={handleSelection}
                        rowSelectionModel={selectionModel}
                        getRowClassName={(params) => `super-app-theme--${params.row.isUndefined && !isFeatureEnabled(featuresQuery, "HideBackgroundColorForUndefinedStops") ? "undefined-stop" : ""}`}
                        pagination
                        disableRowSelectionOnClick // > ver 6.0
                        disableSelectionOnClick // < ver 6.0
                        localeText={translations.components.MuiDataGrid.defaultProps.localeText}
                        columnVisibilityModel={columnVisibilityModel}
                        slotProps={{
                            panel: {
                            sx: {
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                    backgroundColor: "#799c4a",
                                    opacity: 0.3,
                                },
                                "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
                                    backgroundColor: "#799c4a",
                                },
                                "& .MuiDataGrid-panelFooter .MuiButtonBase-root": {
                                    color: colors.primary[200],
                                    fontSize: "0.75rem",
                                    "&:hover": {
                                        color: colors.primary[300],
                                    },
                                },
                                "& .MuiFormLabel-root.Mui-focused": {
                                    color: colors.primary[200],
                                },
                            },
                            }
                        }}
                    />

                    <Popup
                        openPopup={openPopup}
                        setOpenPopup={setOpenPopup}
                        maxWidht="sm"
                        title={<FormattedMessage {...messages.editStop} />}>
                        <MachineStopEditDialog
                            stopReasonCodes={filteredStopReasonCodes}
                            refresh={props.handleRefresh}
                            refetchActivities={props.refetchActivities}
                            machineStop={machineStop}
                            handleSplit={handleSplit}
                            setOpenPopup={setOpenPopup}
                            setShowBackdrop={setShowBackdrop}
                            machine={props.machine}
                            mostFrequentStopReasonCodes={props.mostFrequentStopReasonCodes} />
                    </Popup>
                </Box>
                <AxionControls.AxionBackdrop open={showBackdrop} />
            </>
        )
    }
}

export default MachineStopGrid;